import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import AppShell from "@/components/layout/AppShell";
import { api } from "@/lib/api";
import { useI18n } from "@/contexts/I18nContext";
import { Search, Building2, Instagram, TrendingUp, Users2, X } from "lucide-react";
import { toast } from "sonner";

function ClientDrawer({ id, onClose }) {
  const { lang } = useI18n();
  const [detail, setDetail] = useState(null);
  useEffect(() => {
    if (!id) return;
    api.get(`/clients/${id}`).then((r) => setDetail(r.data));
  }, [id]);
  if (!id) return null;
  return createPortal(
    <div className="fixed inset-0 z-[100] flex" data-testid="client-drawer">
      <div className="flex-1 bg-black/60" onClick={onClose} />
      <div className="w-full max-w-lg bg-background border-s border-border overflow-y-auto">
        <div className="sticky top-0 bg-background border-b border-border/60 px-5 py-3 flex items-center justify-between z-10">
          <div className="font-display text-lg">{lang === "ar" ? "تفاصيل العميل" : "Client details"}</div>
          <button data-testid="client-drawer-close" onClick={onClose} className="p-2 -m-2 hover:bg-muted rounded-sm"><X className="w-4 h-4" /></button>
        </div>
        {!detail ? <div className="p-8 text-center text-muted-foreground">Loading…</div> : (
          <div className="p-5 space-y-4">
            <div>
              <div className="text-xs uppercase tracking-widest text-muted-foreground">Account</div>
              <div className="mt-1 font-medium">{detail.client.name}</div>
              <div className="text-sm text-muted-foreground">{detail.client.email}</div>
            </div>
            {detail.profile && (
              <div>
                <div className="text-xs uppercase tracking-widest text-muted-foreground">{lang === "ar" ? "النشاط" : "Business"}</div>
                <div className="mt-1 font-medium">{detail.profile.business_name}</div>
                <div className="text-sm text-muted-foreground">{detail.profile.industry}</div>
                {detail.profile.description && <div className="mt-2 text-sm">{detail.profile.description}</div>}
                {detail.profile.goals?.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {detail.profile.goals.map((g) => <span key={g} className="text-[10px] px-2 py-0.5 border border-border rounded-sm">{g}</span>)}
                  </div>
                )}
              </div>
            )}
            {detail.subscription && (
              <div className="p-3 border border-border rounded-sm">
                <div className="text-xs uppercase tracking-widest text-muted-foreground">{lang === "ar" ? "الاشتراك" : "Subscription"}</div>
                <div className="mt-1 font-medium">{detail.subscription.plan_name} · {detail.subscription.billing_cycle}</div>
                <div className="text-xs text-muted-foreground mt-1">${detail.subscription.amount} · {detail.subscription.status}</div>
              </div>
            )}
            {detail.growth_plan && (
              <div>
                <div className="text-xs uppercase tracking-widest text-muted-foreground">{lang === "ar" ? "خطة النمو" : "Growth plan"}</div>
                <div className="mt-1 flex items-center gap-2">
                  <span className="font-display text-2xl">{detail.growth_plan.growth_score}</span>
                  <span className="text-xs text-muted-foreground">{detail.growth_plan.score_reasoning}</span>
                </div>
                <div className="mt-2 text-sm">🎯 {detail.growth_plan.next_milestone}</div>
              </div>
            )}
            {detail.social_accounts.length > 0 && (
              <div>
                <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">{lang === "ar" ? "الحسابات الاجتماعية" : "Social accounts"}</div>
                <div className="space-y-1.5">
                  {detail.social_accounts.map((s) => (
                    <div key={s.id} className="flex items-center justify-between text-sm p-2 border border-border/60 rounded-sm">
                      <span>{s.platform} · @{s.handle}</span>
                      <span className="text-muted-foreground text-xs">{s.followers.toLocaleString()}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {detail.recent_posts.length > 0 && (
              <div>
                <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">{lang === "ar" ? "آخر المنشورات" : "Recent posts"}</div>
                <div className="space-y-1.5">
                  {detail.recent_posts.map((p) => (
                    <div key={p.id} className="text-sm p-2 border border-border/60 rounded-sm">
                      <div className="flex items-center justify-between">
                        <span className="font-medium truncate">{p.title}</span>
                        <span className="text-[10px] uppercase text-muted-foreground">{p.status}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}

export default function ClientsPage() {
  const { lang } = useI18n();
  const [clients, setClients] = useState([]);
  const [q, setQ] = useState("");
  const [industry, setIndustry] = useState("");
  const [industries, setIndustries] = useState([]);
  const [openId, setOpenId] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.append("q", q);
      if (industry) params.append("industry", industry);
      const { data } = await api.get(`/clients?${params.toString()}`);
      setClients(data.clients);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
    setLoading(false);
  };
  useEffect(() => { load(); }, [industry]);
  useEffect(() => { api.get("/clients/meta/industries").then((r) => setIndustries(r.data.industries)).catch(() => {}); }, []);

  const searchSubmit = (e) => { e.preventDefault(); load(); };

  return (
    <AppShell>
      <div className="mb-6 flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-display text-3xl">{lang === "ar" ? "العملاء" : "Clients"}</h1>
          <p className="text-muted-foreground text-sm mt-1">
            {lang === "ar" ? "دليل عملاء دعم — الأنشطة، الاشتراكات، وخطط النمو." : "Directory of DAAM clients — businesses, subscriptions, and growth plans."}
          </p>
        </div>
        <div className="text-xs uppercase tracking-widest text-muted-foreground">
          {clients.length} {lang === "ar" ? "عميل" : "clients"}
        </div>
      </div>

      <div className="nx-card p-4 mb-4 flex flex-col md:flex-row gap-3" data-testid="clients-toolbar">
        <form onSubmit={searchSubmit} className="flex-1 flex items-center gap-2 bg-background border border-border rounded-sm px-3">
          <Search className="w-4 h-4 text-muted-foreground" />
          <input data-testid="clients-search" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder={lang === "ar" ? "ابحث بالاسم، النشاط، البريد..." : "Search by name, business, email..."}
            className="flex-1 py-2 outline-none bg-transparent text-sm" />
          <button data-testid="clients-search-btn" type="submit" className="text-xs px-2 py-1 text-primary">{lang === "ar" ? "بحث" : "Search"}</button>
        </form>
        <select data-testid="clients-industry-filter" value={industry} onChange={(e) => setIndustry(e.target.value)}
          className="bg-background border border-border rounded-sm px-3 py-2 text-sm outline-none">
          <option value="">{lang === "ar" ? "كل المجالات" : "All industries"}</option>
          {industries.map((i) => <option key={i} value={i}>{i}</option>)}
        </select>
      </div>

      <div className="nx-card overflow-hidden" data-testid="clients-table">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-widest text-muted-foreground bg-muted/30">
              <tr>
                <th className="text-start px-4 py-3">{lang === "ar" ? "العميل" : "Client"}</th>
                <th className="text-start px-4 py-3">{lang === "ar" ? "النشاط" : "Business"}</th>
                <th className="text-start px-4 py-3">{lang === "ar" ? "الخطة" : "Plan"}</th>
                <th className="text-start px-4 py-3">{lang === "ar" ? "الحسابات" : "Accounts"}</th>
                <th className="text-start px-4 py-3">{lang === "ar" ? "المنشورات" : "Posts"}</th>
                <th className="text-end px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={6} className="py-8 text-center text-muted-foreground">Loading…</td></tr>}
              {!loading && clients.length === 0 && (
                <tr><td colSpan={6} className="py-8 text-center text-muted-foreground">{lang === "ar" ? "لا يوجد عملاء مطابقون." : "No matching clients."}</td></tr>
              )}
              {clients.map((c) => (
                <tr key={c.id} data-testid={`client-row-${c.id}`} className="border-t border-border/40 hover:bg-muted/30">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-sm bg-primary/10 text-primary text-xs font-semibold flex items-center justify-center">
                        {(c.name || c.email).slice(0, 2).toUpperCase()}
                      </div>
                      <div className="min-w-0">
                        <div className="font-medium truncate">{c.name || "—"}</div>
                        <div className="text-xs text-muted-foreground truncate">{c.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    {c.profile ? (
                      <div>
                        <div className="flex items-center gap-1"><Building2 className="w-3 h-3 text-muted-foreground" /><span className="truncate">{c.profile.business_name}</span></div>
                        <div className="text-xs text-muted-foreground">{c.profile.industry}</div>
                      </div>
                    ) : <span className="text-xs text-muted-foreground italic">{lang === "ar" ? "لم يكمل الإعداد" : "Not onboarded"}</span>}
                  </td>
                  <td className="px-4 py-3">
                    {c.subscription ? (
                      <span className="text-[10px] uppercase tracking-widest px-2 py-1 border border-primary/40 text-primary rounded-sm">{c.subscription.plan_name}</span>
                    ) : <span className="text-xs text-muted-foreground">—</span>}
                  </td>
                  <td className="px-4 py-3"><span className="inline-flex items-center gap-1"><Instagram className="w-3 h-3 text-muted-foreground" />{c.stats.social_accounts}</span></td>
                  <td className="px-4 py-3">{c.stats.posts}</td>
                  <td className="px-4 py-3 text-end">
                    <button data-testid={`client-view-${c.id}`} onClick={() => setOpenId(c.id)} className="text-primary text-xs hover:underline">
                      {lang === "ar" ? "عرض" : "View"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <ClientDrawer id={openId} onClose={() => setOpenId(null)} />
    </AppShell>
  );
}
